import React, { useState } from 'react';
import CardItem from '../card/CardItem';
import CardName from '../card/CardName';
import AddToDeckButton from './AddToDeckButton';
import DeckQuantityButton from './DeckQuantityButton';
import Button from '../common/Button';
import cardRoles from '../../utils/cardRoles';
import { useCollection } from '../collection/CollectionContext';
import { useWishlist } from '../wishlist/WishlistContext';

// Same recipe as the role chips, minus the color.
const TAG_CLASS = 'text-white text-xs font-semibold px-2 py-1 rounded-md';

const EdhrecCardList = props => {
  const [showList, setShowList] = useState(true);
  // Ownership comes straight from the contexts, so the tags update the moment a
  // card is added to the collection or the wishlist — no refetch needed.
  const { quantities } = useCollection();
  const { isWishlisted } = useWishlist();

  if (!props.cards.length) return null;

  // Deck copies are keyed by lowercased name (buildDeckQuantities), which is what
  // makes this work despite stored names being title-cased by formatCardName
  // while the textarea holds whatever the user typed.
  const deckCount = card =>
    (props.deckQuantities && props.deckQuantities[card.name.toLowerCase()]) ||
    0;
  const isInDeck = card => deckCount(card) > 0;

  // Only the deck's own lists count copies. The same quantities reach the
  // recommendation and combo lists for the "En el mazo" tag, and counting copies
  // there would report a 100-card list as 110 just because you own 11 Forests.
  const headerCount = props.countCopies
    ? props.cards.reduce((sum, card) => sum + (deckCount(card) || 1), 0)
    : props.cards.length;

  // The deck's own lists steer deck copies instead of collection copies; the rest
  // fall back to CardItem/CardName's default collection stepper.
  const buildQuantityControl = card =>
    props.onDeckQuantityChange ? (
      <DeckQuantityButton
        quantity={deckCount(card)}
        onChange={quantity => props.onDeckQuantityChange(card, quantity)}
      />
    ) : null;

  return (
    <div>
      <div
        className={`flex flex-row justify-between items-center bg-mediumColor p-4 text-white cursor-pointer ${
          showList ? 'rounded-t-md' : 'rounded-md'
        }`}
        onClick={() => setShowList(!showList)}>
        <span className="text-lg font-semibold">
          {props.title} ({headerCount} carta
          {headerCount > 1 ? 's' : ''})
        </span>
        <div className="flex items-center gap-4">
          {props.link && (
            // stopPropagation so following the link doesn't also collapse the
            // list. `!w-auto` beats Button's base w-full without depending on
            // Tailwind's class ordering.
            <a
              href={props.link}
              target="_blank"
              rel="noopener noreferrer"
              onClick={e => e.stopPropagation()}
              className="shrink-0">
              <Button
                className="!w-auto px-4 whitespace-nowrap"
                iconClass="fas fa-external-link-alt">
                {props.linkLabel || 'Ver en EDHREC'}
              </Button>
            </a>
          )}
          {showList ? (
            <i className="fas fa-angle-double-up"></i>
          ) : (
            <i className="fas fa-angle-double-down"></i>
          )}
        </div>
      </div>
      {showList && (
        <div
          className={`bg-lightColor rounded-b-md p-4 grid ${
            props.showCards
              ? 'grid-cols-1 md:grid-cols-3 lg:grid-cols-4 2xl:grid-cols-5 gap-4'
              : 'grid-cols-1 gap-y-4'
          }`}>
          {props.cards.map(card => (
            <div key={card.id} className="relative flex flex-col gap-y-2">
              <div className="absolute top-2 left-2 z-10 flex flex-col items-start gap-1 max-w-[70%]">
                {deckCount(card) > 0 && (
                  <span className={`bg-accentDark ${TAG_CLASS}`}>
                    En el mazo (x{deckCount(card)})
                  </span>
                )}
                {quantities[card.id] > 0 && (
                  <span className={`bg-emerald-600 ${TAG_CLASS}`}>
                    En colección (x{quantities[card.id]})
                  </span>
                )}
                {isWishlisted(card.id) && (
                  <span className={`bg-pink-600 ${TAG_CLASS}`}>
                    En wishlist
                  </span>
                )}
              </div>
              {card.roles && card.roles.length > 0 && (
                <div className="absolute top-2 right-2 z-10 flex flex-wrap gap-1 justify-end max-w-[70%]">
                  {card.roles.map(role => (
                    <span
                      key={role}
                      className={`${
                        cardRoles.ROLE_COLORS[role] || 'bg-gray-700'
                      } ${TAG_CLASS}`}>
                      {cardRoles.ROLE_LABELS[role] || role}
                    </span>
                  ))}
                </div>
              )}
              {props.showCards ? (
                <CardItem
                  card={card}
                  quantityControl={buildQuantityControl(card)}></CardItem>
              ) : (
                <CardName
                  card={card}
                  quantityControl={buildQuantityControl(card)}></CardName>
              )}
              {props.onAddToDeck && (
                <AddToDeckButton
                  isInDeck={isInDeck(card)}
                  onAdd={() => props.onAddToDeck(card)}
                />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default EdhrecCardList;
